// Small inline icon set used throughout the nav/admin UI. Named after their
// closest Bootstrap Icons equivalent, but hand-drawn with basic shapes
// rather than copied path data -- a misremembered complex path is how the
// first camera-reels-fill attempt ended up looking like two people instead
// of a camera. Two rendering styles, chosen deliberately per icon:
//
//   - Solid (fill="currentColor", no inner cutouts): safe ANYWHERE,
//     including spots whose background changes on hover (admin table rows,
//     dropdown buttons) -- there's nothing in the shape that depends on
//     matching a background color.
//   - Outline (fill="none" stroke="currentColor"): also background-safe,
//     used for icons that read better as line art (file-based ones).
//   - A few nav-bar-only icons (calendar/camera) use a small
//     fill="var(--panel)" cutout for a "punched hole" detail -- safe ONLY
//     because nav's own background is a fixed var(--panel) with no hover
//     state, verified against styles.css before using this trick.

type IconProps = React.SVGProps<SVGSVGElement>;

/** Bootstrap Icons' "envelope-at-fill" -- a solid envelope (the flap is the
 *  shape's own top-edge geometry, not a color-matched cutout) with a small
 *  stroke-drawn @ mark, so it stays correct against any background. */
export function EnvelopeAtFillIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" {...props}>
      <path fill="currentColor" d="M1 3 8 7.2 15 3v10H1Z" />
      <circle cx="8" cy="5.3" r="1.15" fill="none" stroke="currentColor" strokeWidth="0.7" />
      <path d="M9.15 5.3v0.75c0 .5.42.88.9.68.46-.2.7-.75.58-1.25" fill="none" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" />
    </svg>
  );
}

/** Bootstrap Icons' "envelope-at" -- the outline counterpart to
 *  EnvelopeAtFillIcon above: same envelope silhouette and @ mark, but a
 *  line-art border instead of a solid fill, with the @ moved down into the
 *  open body below the flap crease so it doesn't fight the flap lines for
 *  the same space the way the solid version's overlap trick relies on. */
export function EnvelopeAtIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" {...props}>
      <rect x="1" y="3" width="14" height="10" rx="0.5" fill="none" stroke="currentColor" strokeWidth="1" />
      <path d="M1 3 8 7.2 15 3" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="8" cy="9.3" r="1.15" fill="none" stroke="currentColor" strokeWidth="0.7" />
      <path d="M9.15 9.3v0.75c0 .5.42.88.9.68.46-.2.7-.75.58-1.25" fill="none" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" />
    </svg>
  );
}

export function FacebookIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951" />
    </svg>
  );
}

/** Account menu trigger (Bootstrap Icons' "person-lines-fill") -- a bust
 *  plus three "line" marks, read as a contact-card/directory glyph. Solid,
 *  no cutouts, so it's safe in the dropdown trigger regardless of hover. */
export function PersonLinesFillIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" {...props}>
      <circle cx="4.5" cy="3.5" r="2.3" />
      <path d="M1 14c0-3 1.6-5 3.5-5s3.5 2 3.5 5z" />
      <rect x="9.3" y="3.6" width="5.7" height="1.1" rx="0.3" />
      <rect x="9.3" y="7.1" width="5.7" height="1.1" rx="0.3" />
      <rect x="9.3" y="10.6" width="4.2" height="1.1" rx="0.3" />
    </svg>
  );
}

/** Outline style (Bootstrap's "camera-video" has no -fill suffix) -- a
 *  camera body with a trapezoidal viewfinder/zoom flap, stroke-only so it's
 *  correct against any background. */
export function CameraVideoIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true" {...props}>
      <rect x="1" y="4" width="8.5" height="8" rx="1.2" />
      <path d="M9.5 7 14.5 4.3v7.4L9.5 9Z" />
    </svg>
  );
}

/** Sign-out glyph (Bootstrap Icons' "box-arrow-right"). */
export function BoxArrowRightIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" {...props}>
      <path fillRule="evenodd" d="M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 2h-8A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0z" />
      <path fillRule="evenodd" d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708z" />
    </svg>
  );
}

/** Nav-only (fixed var(--panel) background, no hover state -- see header
 *  comment). Two ring tabs, a calendar body, and a punched horizontal bar in
 *  the nav's own panel color standing in for a highlighted date range
 *  (Bootstrap Icons' "calendar3-range-fill"). */
export function Calendar3RangeFillIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" {...props}>
      <rect x="3.4" y="0" width="1.6" height="3.2" rx="0.8" />
      <rect x="11" y="0" width="1.6" height="3.2" rx="0.8" />
      <rect x="1" y="2" width="14" height="13" rx="1.6" />
      <rect x="3" y="9.2" width="10" height="2.6" rx="0.6" fill="var(--panel)" />
    </svg>
  );
}

/** Outline style (Bootstrap's "newspaper" has no -fill suffix) -- a page
 *  outline with a photo block and a few text-line rules. Stroke-only, so
 *  it's correct against any background. */
export function NewspaperIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" aria-hidden="true" {...props}>
      <rect x="1.2" y="2.8" width="13.6" height="10.4" rx="1" />
      <rect x="3" y="4.6" width="4.2" height="3.6" rx="0.3" />
      <line x1="8.6" y1="4.8" x2="12.2" y2="4.8" />
      <line x1="8.6" y1="6.6" x2="12.2" y2="6.6" />
      <line x1="3" y1="9.6" x2="12.2" y2="9.6" />
      <line x1="3" y1="11.2" x2="12.2" y2="11.2" />
    </svg>
  );
}

// Shared "file with a folded corner" outline, reused by FilePersonIcon and
// FileEarmarkArrowUpIcon below (both are outline-style in Bootstrap's own
// naming -- neither has a -fill suffix).
const FILE_OUTLINE_PATH = "M9.5 0.7H3.3a1 1 0 0 0-1 1v12.6a1 1 0 0 0 1 1h9.4a1 1 0 0 0 1-1V5.2L9.5 0.7z";
const FILE_FOLD_PATH = "M9.5 0.7V5.2h4.2";

/** Bootstrap Icons' "file-person" (outline). */
export function FilePersonIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true" {...props}>
      <path d={FILE_OUTLINE_PATH} />
      <path d={FILE_FOLD_PATH} />
      <circle cx="8" cy="9.2" r="1.5" />
      <path d="M5.4 13c0-1.2 1.2-2 2.6-2s2.6 0.8 2.6 2" />
    </svg>
  );
}

/** Bootstrap Icons' "file-earmark-arrow-up" (outline). */
export function FileEarmarkArrowUpIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true" {...props}>
      <path d={FILE_OUTLINE_PATH} />
      <path d={FILE_FOLD_PATH} />
      <path d="M8 12.2V7.4" />
      <path d="M5.8 9.4 8 7.2l2.2 2.2" />
    </svg>
  );
}

/** Bootstrap Icons' "car-front-fill" (solid, no cutouts -- used inside the
 *  account dropdown, whose rows change background on hover). */
export function CarFrontFillIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" {...props}>
      <rect x="2" y="2" width="12" height="5" rx="1.6" />
      <rect x="1" y="6.6" width="14" height="5.4" rx="2" />
      <circle cx="3.4" cy="13" r="1.6" />
      <circle cx="12.6" cy="13" r="1.6" />
    </svg>
  );
}

/** Bootstrap Icons' "key-fill" (solid, no cutouts -- same dropdown-hover
 *  reasoning as the car icon above). */
export function KeyFillIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" {...props}>
      <circle cx="4" cy="4" r="3.6" />
      <rect x="6.8" y="3.1" width="8.2" height="1.8" rx="0.4" />
      <rect x="11.6" y="4.9" width="1.8" height="2.6" />
      <rect x="13.6" y="4.9" width="1.4" height="2.6" />
    </svg>
  );
}

/** Outline style (Bootstrap's "geo-alt" has no -fill suffix) -- a map-pin
 *  teardrop with a hollow ring, both stroke-only, so it's correct against
 *  any background. */
export function GeoAltIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M8 15S13.5 9.8 13.5 6A5.5 5.5 0 0 0 2.5 6C2.5 9.8 8 15 8 15Z" />
      <circle cx="8" cy="6" r="2" />
    </svg>
  );
}

/** Solid (Bootstrap's "person-raised-hand") -- a bust with one arm raised,
 *  used for the About/Contact nav link. No cutouts, safe anywhere. */
export function PersonRaisedHandIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" {...props}>
      <circle cx="7" cy="3" r="2.1" />
      <path d="M3.5 15c0-3 1.5-5 3.5-5s3.5 2 3.5 5z" />
      <path d="M9.3 8.3 12.8 3.6a1 1 0 0 1 1.6 1.2L11.2 9.4Z" />
    </svg>
  );
}

/** Outline-style storefront (Bootstrap's "shop" -- no -fill suffix): a
 *  scalloped awning over a building body with a door. Stroke-only, so it's
 *  correct against any background. */
export function ShopIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true" {...props}>
      <path d="M1.3 2.6h13.4l0.8 3c0 1.1-0.9 2-2 2s-2-0.9-2-2c0 1.1-0.9 2-2 2s-2-0.9-2-2c0 1.1-0.9 2-2 2s-2-0.9-2-2z" />
      <path d="M2.2 7.5V14h11.6V7.5" />
      <path d="M6.4 14V9.8h3.2V14" />
    </svg>
  );
}

/** Bootstrap Icons' "house-fill" -- roof + walls as one path, with a
 *  rectangular notch standing in for the door. That notch is plain path
 *  geometry (the outline dips in and back out), not a background-matched
 *  cutout, so unlike the nav-only icons above this one is safe anywhere. */
export function HouseFillIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M8 1 1 7v8h5v-5h4v5h5V7Z" />
    </svg>
  );
}

/** Cart indicator glyph (solid, no cutouts). */
export function CartFillIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5z" />
      <circle cx="5" cy="14.5" r="1.3" />
      <circle cx="12" cy="14.5" r="1.3" />
    </svg>
  );
}

/** Bootstrap Icons' "trash3-fill" -- simplified to a handle, a lid bar, and
 *  a tapered solid can (no inner rib cutouts), so unlike the nav-only
 *  punched-hole icons this one is safe on any background, including a red
 *  danger button's fill. */
export function Trash3FillIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" {...props}>
      <rect x="6" y="0.3" width="4" height="1.4" rx="0.6" />
      <rect x="2.3" y="2.1" width="11.4" height="1.7" rx="0.8" />
      <path d="M3.4 4.3h9.2l-0.85 10.2a1.3 1.3 0 0 1-1.3 1.2H5.55a1.3 1.3 0 0 1-1.3-1.2Z" />
    </svg>
  );
}

/** Bootstrap Icons' "pencil-fill" -- simplified to an eraser cap, a shaft,
 *  and a pointed tip, built upright then rotated 45deg rather than drawn
 *  diagonally by hand, so the three pieces stay easy to reason about. */
export function PencilFillIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" {...props}>
      <g transform="rotate(45 8 8)">
        <rect x="6.8" y="1" width="2.4" height="3" rx="0.6" />
        <rect x="6.8" y="4" width="2.4" height="8.5" />
        <path d="M6.8 12.5h2.4L8 15.2Z" />
      </g>
    </svg>
  );
}

/** Outline "question-circle": a stroked ring with a "?" drawn as a stroked
 *  hook plus a dot -- no cutouts, so it's correct against any background. */
export function QuestionCircleIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" aria-hidden="true" {...props}>
      <circle cx="8" cy="8" r="7" strokeWidth="1.3" />
      <path d="M6 6.2a2 2 0 1 1 2.9 1.8c-.6.3-.9.7-.9 1.3v.5" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="8" cy="11.8" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Outline "clipboard-check" for the Manage menu item: stroked board, solid
 *  clip on top, stroked check mark -- no cutouts, safe on any background. */
export function ClipboardCheckIcon(props: IconProps) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" aria-hidden="true" {...props}>
      <rect x="2.6" y="2.4" width="10.8" height="12.8" rx="1.2" strokeWidth="1.3" />
      <rect x="5.4" y="0.9" width="5.2" height="2.8" rx="0.6" fill="currentColor" stroke="none" />
      <path d="M5.3 9.2l1.9 1.9 3.6-3.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
